import type { Role } from '@/types';
import { AccordionGroup } from '@nautilusbio/nb-design-system/AccordionGroup';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import React, { type ChangeEvent, type FC, memo, useMemo } from 'react';

import styles from '../../styles.module.css';

import { ORG_LIST_MAX_HEIGHT_PX, SEARCH_THRESHOLD } from './constants';
import { useOrgSearch } from './hooks';
import { OrgListItem } from './OrgListItem';
import type { OrgSelectorContentProps } from './types';

/** Popover body: one flat, independently scrolling Organizations list in the API's own order,
 *  current org included. See README.md. */
export const OrgSelectorContent: FC<OrgSelectorContentProps> = memo(
  ({ selectedOrg, orgs, allowedRolesByOrg, assumedRolesByOrg, onSwitch, isPending, tOrgs }) => {
    // The org list comes from a 30-day-cached query, so it can lag the org actually selected
    // (access revoked, another user on this browser). Never let the current org go unlisted.
    const listedOrgs = useMemo(
      () => (orgs.some(o => o.id === selectedOrg.id) ? orgs : [selectedOrg, ...orgs]),
      [orgs, selectedOrg],
    );

    const { query, setQuery, filtered } = useOrgSearch(listedOrgs);
    const showSearch = listedOrgs.length > SEARCH_THRESHOLD;

    const searchLabel = {
      children: tOrgs('org_selector_search_placeholder'),
      id: 'org-search-label',
      htmlFor: 'org-search-input',
    };

    return (
      <Stack gap={4} orientation="vertical">
        <Text as="h4">{tOrgs('org_selector_organizations')}</Text>

        {showSearch && (
          <Input
            data-testid="org-search-input"
            fullWidth
            id="org-search-input"
            label={searchLabel}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
            placeholder={tOrgs('org_selector_search_placeholder')}
            size="xs2"
            value={query}
          />
        )}

        <AccordionGroup
          allowsMultipleExpanded
          className={styles.orgListContainer}
          orientation="vertical"
          style={{ maxHeight: ORG_LIST_MAX_HEIGHT_PX }}
        >
          {filtered.map(org => (
            <OrgListItem
              key={org.id}
              allowedRoles={rolesFor(org.id, allowedRolesByOrg, assumedRolesByOrg)}
              assumedRole={assumedRolesByOrg[org.id]}
              isPending={isPending}
              isSelected={org.id === selectedOrg.id}
              org={org}
              onSwitch={onSwitch}
            />
          ))}
          {filtered.length === 0 && <Text as="span">{tOrgs('org_selector_no_results')}</Text>}
        </AccordionGroup>
      </Stack>
    );
  },
);

OrgSelectorContent.displayName = 'OrgSelectorContent';

/** Mirrors useCurrentOrgAllowedRoles' union: `allowed_roles` is all-or-nothing, so an org the user
 *  provably holds an assumed role in can be missing from the grant map entirely. */
function rolesFor(
  orgId: string,
  allowedRolesByOrg: Record<string, readonly Role[]>,
  assumedRolesByOrg: Record<string, Role>,
): readonly Role[] {
  const granted = allowedRolesByOrg[orgId] ?? [];
  const assumed = assumedRolesByOrg[orgId];
  return assumed && !granted.includes(assumed) ? [assumed, ...granted] : granted;
}
